export type ProfileTab = "personal" | "orders" | "transactions";

export const PROFILE_TABS: ProfileTab[] = [
    "personal",
    "orders",
    "transactions",
];

export const DEFAULT_PROFILE_TAB: ProfileTab = "personal";

export const PROFILE_TAB_ID: Record<ProfileTab, string> = {
    personal: "profile-tab-personal",
    orders: "profile-tab-orders",
    transactions: "profile-tab-transactions",
};

export const PROFILE_TAB_PANEL_ID: Record<ProfileTab, string> = {
    personal: "profile-tabpanel-personal",
    orders: "profile-tabpanel-orders",
    transactions: "profile-tabpanel-transactions",
};

const LEGACY_PROFILE_TAB_ALIASES: Record<string, ProfileTab> = {
    balance: DEFAULT_PROFILE_TAB,
};

export function isProfileTab(value: string): value is ProfileTab {
    return (PROFILE_TABS as string[]).includes(value);
}

/** Maps unknown or legacy hash segments (e.g. removed `balance` tab) to a visible tab. */
export function resolveVisibleProfileTab(tab: string): ProfileTab {
    if (isProfileTab(tab)) {
        return tab;
    }
    return LEGACY_PROFILE_TAB_ALIASES[tab] ?? DEFAULT_PROFILE_TAB;
}

export function profileHash(tab: ProfileTab = DEFAULT_PROFILE_TAB): string {
    return `profile/${tab}`;
}

export function isProfileMenu(menu: string): boolean {
    return menu === "profile" || menu.startsWith("profile/");
}

export function getProfileTabFromMenu(menu: string): ProfileTab {
    const match = menu.match(/^profile\/([^/]+)$/);
    if (match) {
        return resolveVisibleProfileTab(match[1]);
    }
    return DEFAULT_PROFILE_TAB;
}

export type ParsedAppHash = {
    menu: string;
    profileTab: ProfileTab;
};

/** True when the URL hash is a Viva basket payment success return. */
export function isBasketPaymentSuccessHash(hash: string): boolean {
    const clean = hash.replace(/^#/, "");
    return clean === "paySuccess";
}

/** Parse `window.location.hash` into main menu + profile tab (when on account). */
export function parseAppHash(hash: string): ParsedAppHash {
    let clean = hash.replace(/^#/, "");
    if (isBasketPaymentSuccessHash(hash)) {
        return { menu: "paySuccess", profileTab: DEFAULT_PROFILE_TAB };
    }
    if (clean.includes("payFail")) {
        return { menu: "payFail", profileTab: DEFAULT_PROFILE_TAB };
    }
    if (clean.startsWith("profile/")) {
        return { menu: "profile", profileTab: getProfileTabFromMenu(clean) };
    }
    if (clean === "profile") {
        return { menu: "profile", profileTab: DEFAULT_PROFILE_TAB };
    }
    return { menu: clean, profileTab: DEFAULT_PROFILE_TAB };
}
